import { Country } from '../types/master-data.types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Edit, Trash2 } from 'lucide-react';

interface CountryTableProps {
  countries: Country[];
  onEdit: (country: Country) => void;
  onDelete: (country: Country) => void;
  isLoading?: boolean;
}

export function CountryTable({ countries, onEdit, onDelete, isLoading }: CountryTableProps) {
  if (isLoading) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Cargando países...</p>
      </div>
    );
  }

  if (!countries || countries.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground font-medium">No hay países configurados</p>
        <p className="text-xs text-muted-foreground mt-1">
          Agrega países para segmentar cotizaciones por mercado y aplicar reglas de pricing por
          region
        </p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Código ISO</TableHead>
            <TableHead>Nombre</TableHead>
            <TableHead>SAP</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead className="text-right">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {countries.map((country) => (
            <TableRow key={country.id}>
              <TableCell className="font-mono font-semibold">{country.code}</TableCell>
              <TableCell>{country.name}</TableCell>
              <TableCell className="font-mono text-xs text-muted-foreground">
                {country.sapCode || '—'}
              </TableCell>
              <TableCell>
                <Badge variant={country.isActive ? 'success' : 'default'}>
                  {country.isActive ? 'Activo' : 'Inactivo'}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button size="sm" variant="ghost" onClick={() => onEdit(country)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Editar nombre o código ISO del país</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button size="sm" variant="ghost" onClick={() => onDelete(country)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Eliminar este país</TooltipContent>
                  </Tooltip>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default CountryTable;
